<script setup>
import { onActivated, onMounted, onUnmounted, defineProps } from "vue"
import { ref } from "@vue/reactivity"
import { KNOLIA } from "@/knolia"
import { Gems } from "@/gems"
import { defineEmits } from "vue"
import { MESSAGES } from "../messages"
import Profile from "../components/Profile.vue"
import { useRouter } from "vue-router"

const defaultImage = ref(require('@/images/default.png'))
const props = defineProps({
  userStore: {
    type: Object,
    default: Object
  }
})

defineEmits(['force-reload'])

const router = useRouter();
const arrUsers = ref([])
const abortController = ref(null)
const txtUser = ref('')
const actualHeadship = ref(null)
const sitemapArr = ref([{name: `${props.userStore.User.permissions.is_admin ? 'CEO\'s' : 'Meus Subordinados'}`, id: null}])
let hasLoadedHierarchy = false
const showInactives = ref(false)
const reload = ref(false)
let canShowProfileModal = ref(false)
const isLowerThanMobile = ref(KNOLIA.updateIsLowerThanMobile())
const isMobile = ref(KNOLIA.updateIsMobile())

function GoToSubordinates(user) {
    if(txtUser.value != '')
        sitemapArr.value = [{name: `${props.userStore.User.permissions.is_admin ? 'CEO\'s' : 'Meus Subordinados'}`, id: null}]

    sitemapArr.value.push(user)
    GetHierarchy(user.id)
}

function GetSitemapSubordinates(userId, index) {
    const soma = parseInt(index, 10);
    const max = sitemapArr.value.length - soma;
    sitemapArr.value.splice(soma, max);
    
    GetHierarchy(userId)
}

async function GetHierarchy(user_id = null) {
    if(user_id)
        actualHeadship.value = user_id

    const res = await KNOLIA.CallBackEnd("get", "get-hierarchy", {
        params: {
            user_id
        },
    })

    if(res.error) {
        Gems.Telegram(MESSAGES.ERROR.DEFAULT, 'tele-not-allowed')
        return
    }

    arrUsers.value = res.data
    hasLoadedHierarchy = true
}

async function SearchUsers() {
    if(txtUser.value == "") {
        GetHierarchy(actualHeadship.value)
        return
    }

    if (abortController.value != null) 
        abortController.value.abort();

    abortController.value = new AbortController();

    const res = await KNOLIA.CallBackEnd("get", "filter-hierarchy", {
        params: {
          user_id: actualHeadship.value,
          search_text: txtUser.value,
          signal: abortController.value.signal,
        },
    })
    arrUsers.value = res.data
    arrUsers.value = res.data
}

function ResetSitemap() {
    sitemapArr.value = [{name: `${props.userStore.User.permissions.is_admin ? 'CEO\'s' : 'Meus Subordinados'}`, id: null}]
    actualHeadship.value = null
    txtUser.value = ''

    if (abortController.value) {
        abortController.value.abort()
        abortController.value = null
    }

    GetHierarchy()
}

function ShowInactives() {
    reload.value = !reload.value;
}

function ToProfile(user) {
    KNOLIA.SetRequestedUserId(user.id)
    canShowProfileModal.value = true
}

function ToSkillCard(user) {
    KNOLIA.SetRequestedUserId(user.id)
    router.push('/skill-card')
}

function GoToPotential(user) {
    KNOLIA.SetRequestedUserId(user.id)
    router.push('/user-potential')
}

function GoToChallenges(user) {
    KNOLIA.SetRequestedUserId(user.id)
    router.push('/challenges')
}

function HandleUsersMenuNavigation() {
    ResetSitemap()
}

onActivated(() => {
    if (!hasLoadedHierarchy) {
        GetHierarchy()
    }
})

onMounted(() => {
    window.addEventListener('users-menu-navigation', HandleUsersMenuNavigation)
    window.addEventListener('resize', () => {
        isMobile.value = KNOLIA.updateIsMobile()
        isLowerThanMobile.value = KNOLIA.updateIsLowerThanMobile()
    })
})

onUnmounted(() => {
    window.removeEventListener('users-menu-navigation', HandleUsersMenuNavigation)
})

</script>

<template>
    <div class="page-container">
        <div class="div-search-users">
            <input type="text" class="input-user" :class="{'ddl-alt-padding': isMobile || isLowerThanMobile}" placeholder="Pesquisar colaborador..." v-model="txtUser" @keyup="SearchUsers()">
            <div class="div-inactives-checkbox">
                <input class="chk cursor-pointer" type="checkbox" v-model="showInactives" @click="ShowInactives">
                <label>MOSTRAR INATIVOS</label>
            </div>
        </div>

        <div class="sitemap" v-if="sitemapArr.length > 1">
            <span class="name-container" v-for="(sitemap, i) in sitemapArr" :key="i">
                <span class="headship" @click="GetSitemapSubordinates(sitemap.id, i)">
                    {{ sitemap.name }}
                </span>
                <span v-if="++i < sitemapArr.length">
                    <i class="fas fa-chevron-right sitemap-icon" aria-hidden="true" ></i>
                </span>
            </span>
        </div>

        <div class="users-list">
            <div class="user-row row align-items-center" v-for="(user, index) in arrUsers" :key="user.id" :class="{'bg-alt-line': index % 2 == 0, 'user-inactive': !user.active}" v-show="!user.active ? showInactives : true">
                <div :class="{'col-2': isMobile || isLowerThanMobile, 'col-sm-1':!isMobile && !isLowerThanMobile}">
                    <img ref="div-logo" class="div-logo" :src="user.src ?? defaultImage" @error="$event.target.src = defaultImage">
                </div>
                
                <span :class="{'col-4': isMobile || isLowerThanMobile, 'col-sm-3': !isMobile && !isLowerThanMobile}" class="line-clamp line-clamp-1 user-name" @click="ToProfile(user)">{{ user.name }}</span>
                <span :class="{'col-4': isMobile || isLowerThanMobile, 'col-sm-3': !isMobile && !isLowerThanMobile}" class="line-clamp line-clamp-1">{{ user.job }}</span>
                <span class="col-3 line-clamp line-clamp-1" v-if="!isLowerThanMobile && !isMobile">{{ user.category }}</span>
                <span class="col-2 icons gx-1">
                    <!-- Gráfico de barras ascendente com seta -->
                    <i class="bi bi-graph-up-arrow" @mouseenter="Gems.Tooltip($event.target, 'Explorar Potencial', 'tooltip')" @click="GoToPotential(user)"></i>
                    <!-- Foguete subindo -->
                    <!-- <i class="bi bi-rocket-takeoff" @mouseenter="Gems.Tooltip($event.target, 'Explorar Potencial', 'tooltip')"></i> -->
                    <!-- Troféu de conquista e alto rendimento -->
                    <i class="fa-solid fa-trophy" @mouseenter="Gems.Tooltip($event.target, 'Explorar Desafios', 'tooltip')" @click="GoToChallenges(user)"></i>
                    <i class="far fa-address-card" :id="`skill-card${user.id}`" @mouseenter="Gems.Tooltip($event.target, 'Cédula de competências', 'tooltip')" @click="ToSkillCard(user)"></i>
                    <i :class="{inactive: showInactives ? !user.has_subordinates: !user.has_subordinates_active}" class="fas fa-sitemap" :id="`hierarchy${user.id}`" 
                        @mouseenter="Gems.Tooltip($event.target, 'Hierarquia', 'tooltip')" @click="GoToSubordinates(user)"></i>
                </span>
            </div>
        </div>
    </div>

    <Profile v-if="canShowProfileModal" @close-profile-modal="canShowProfileModal = false"/>
</template>

<style scoped>

.div-search-users {
    display: flex;
    margin-bottom: 1%;
    gap: 1%;
}

.div-inactives-checkbox {
    display: flex;
    align-self: center;
    margin-left: 4px;
}

.chk {
    transform: scale(1.5);
    margin-right: 10px;
}

.input-user {
    flex: 1;
    font-style: italic;
}

.sitemap {
    margin-bottom: 1%;
    padding-left: 15px;
}

.sitemap-icon {
    margin: 0 10px;
}

.sitemap .headship:hover {
  color: var(--default-app-hover-color);
  cursor: pointer;
}

.user-row {
    height: 40px;
    margin: 0px 0px 3px;
    border-radius: 5px;
}

.user-name {
    font-weight: bold;
    cursor: pointer;
}

.icons {
    display: flex;
    justify-content: space-evenly;
    align-items: center;
}

.icons i {
    cursor: pointer;
    font-size: 1.3em;
}

.inactive {
    pointer-events: none;
    cursor: default !important;
}

.user-inactive,
.inactive {
    opacity: .2;
}

.div-logo {
  height: 30px;
  width: 31px;
  border-radius: 50%;
  background-size: cover;
  background-position: center;
  box-shadow: 0 0 10px 1px rgb(0, 0, 0, 0.1);
  margin: 0 auto;
  object-fit: cover;
}

@media screen and (max-width: 768px) {

    .page-container {
        padding: 0px;
    }

    .div-search-users {
        padding: 2%;
        margin: 0px;
    }

    .chk {
        transform: scale(1.2);
    }

    .sitemap {
        margin: 0px 2% 2% 2%;
    }
}
</style>
